import { DATE_INPUT_FORMAT, TODAY, TODAY_EOD } from '@/constants';
import { Datetime, createNotification, saveFile } from '@/utils';
import { InputWithIcon } from '@/components/Input';
import { SummaryHistoryFiltersType, useSummaryHistory } from '@/features/SummaryHistory';
import { useCallback, useEffect, useState } from 'react';

import { Button } from 'react-bootstrap';
import DateRangePicker from '@/components/DateRangePicker';
import { DispatchSummaryCallVolumeEnum } from '@/models/gen/graphql';
import { EnumDropdownMulti } from '@/components/EnumDropdown';
import FormButton from '@/components/FormButton';
import PageNavigationDropdown from '@/common/PageNavigationDropdown';
import QueryFilters from '@/components/QueryFilters';
import SummaryHistoryFiltersInfo from '@/features/SummaryHistory/components/SummaryHistoryFilters/SummaryHistoryFiltersInfo';
import { SummaryHistoryFormDefinitions } from '@/features/SummaryHistory/components/SummaryHistoryForm/types';
import { deleteDispatchSummary } from '@/features/SummaryHistory/api/services/deleteDispatchSummary';
import { deleteDispatchSummaryMarket } from '@/features/SummaryHistory/api/services/deleteDispatchSummaryMarket';
import { downloadDispatchSummaryWithCacheCsv } from '@/features/SummaryHistory/api/services/downloadDispatchSummaryCSV';
import useConfirmation from '@/hooks/useConfirmation';
import { useSummaryHistoryDrawer } from '@/features/SummaryHistory/components/SummaryHistoryDrawer';
import Toast from '@/models/Toast';
import { createPersistentComponentState } from '@/state';
import { customSearchStorage } from '@/state/storage';
import usePopState from '@/hooks/usePopState';
import { ServiceIssueFiltersType, useServiceIssueFilters } from '@/hooks/useServiceIssueFilters';

const initSummaryHistoryFiltersState: SummaryHistoryFiltersType = {
  startDatetime: TODAY,
  endDatetime: TODAY_EOD,
  callVolumes: [],
};

const useSummaryHistoryFilters = createPersistentComponentState<SummaryHistoryFiltersType>(
  'useSummaryHistoryFilters',
  initSummaryHistoryFiltersState,
  {
    live: false,
    storage: customSearchStorage,
    reviver: (key: string, value: any) => {
      switch (key) {
        default: {
          return value;
        }
      }
    },
  }
);

// TODO: Add appropriate filters to this component based on the design when it is complete.
const SummaryHistoryFilters = (): JSX.Element => {
  const [{ loading, selected, summaries, search }, setState, { submitFilters }] = useSummaryHistory(
    ({ loading, selected, summaries, search }) => ({
      loading,
      selected,
      summaries,
      search,
    })
  );
  const filtersState = useServiceIssueFilters(({ state }) => state);
  const setFiltersState = useServiceIssueFilters(({ setState }) => setState);
  const saveFiltersState = useServiceIssueFilters(({ saveState }) => saveState);
  const rehydrateFiltersState = useServiceIssueFilters(({ rehydrate }) => rehydrate);
  const [lastQuery, setLastQuery] = useState<ServiceIssueFiltersType>(filtersState);
  const setSummaryHistoryDrawerState = useSummaryHistoryDrawer(({ setState }) => setState);
  const confirmDelete = useConfirmation();

  const handleChange = useCallback(
    (name: string) =>
      (value: unknown): void =>
        setFiltersState((current) => ({ ...current, [name]: value })),
    [setFiltersState]
  );

  const handleSubmit = useCallback((): void => {
    console.log('SummaryHistoryFilters: Submitting...');
    // get the latest state from closure to ensure it's always accurate
    const { state } = useServiceIssueFilters.getState();
    saveFiltersState();
    setLastQuery(state);
    submitFilters(state);
  }, [filtersState, saveFiltersState]);

  const handleReset = useCallback((): void => {
    const startDatetime = new Datetime().startOf('day').toString();
    const endDatetime = new Datetime().endOf('day').toString();
    const resetInput = { ...initSummaryHistoryFiltersState, startDatetime, endDatetime };
    setFiltersState(resetInput);
    setLastQuery(resetInput);
    setState((current) => ({ ...current, search: '' }));
    saveFiltersState();
    submitFilters(resetInput);
  }, [setState, saveFiltersState]);

  const handlePopState = useCallback(() => rehydrateFiltersState().then(handleSubmit), [rehydrateFiltersState, handleSubmit]);
  usePopState(handlePopState);

  const onCreate = () => {
    setSummaryHistoryDrawerState({ show: true, data: SummaryHistoryFormDefinitions.initValue });
  };

  const onDelete = () =>
    confirmDelete()
      .then(async () => {
        try {
          const ids = Array.from(selected.keys()).map((id) => ({ id }));
          const commentIds = Array.from(selected.values())
            .map((summary) =>
              summary.marketComments.map((comment) => ({
                id: comment.id,
              }))
            )
            .flat();
          if (commentIds?.length > 0) {
            await deleteDispatchSummaryMarket(commentIds);
          }
          await deleteDispatchSummary(ids);
          createNotification('Sucessfully Deleted Dispatch Summary', Toast.Type.SUCCESS, 'Dispatch Summary');
          setState((current) => ({
            ...current,
            selected: new Map(),
          }));
        } catch (err) {
          console.error('Error Deleting Lost and Found.', err);
        } finally {
          return submitFilters?.(useServiceIssueFilters.getState().state);
        }
      })
      .catch(() => console.log('Delete cancelled'));
  const onExportToCSV = async () => {
    const { state: csvExportFilters } = useServiceIssueFilters.getState();
    const res = await downloadDispatchSummaryWithCacheCsv([csvExportFilters]);
    if (!res) return;
    const blob = new Blob([res.data], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    saveFile(url, `DispatchSummaryHistory-${new Datetime().format(DATE_INPUT_FORMAT)}.csv`);
  };

  const onOpenSettings = () => {
    //TODO: Implement settings modal/drawer
    console.log('Open Settings');
  };

  useEffect(() => {
    handleSubmit();
  }, []);

  return (
    <>
      <QueryFilters className="AccidentHistoryFilters justify-content-between">
        <div className="d-flex gap-2">
          <QueryFilters.Control>
            <DateRangePicker
              name="dates"
              isDirty={filtersState.startDatetime !== lastQuery.startDatetime || filtersState.endDatetime !== lastQuery.endDatetime}
              value={[filtersState.startDatetime, filtersState.endDatetime]}
              onChange={(event) => {
                const [from, to] = event.target.value.split(' - ') || [null, null];
                handleChange('startDatetime')(from);
                handleChange('endDatetime')(to);
              }}
            />
          </QueryFilters.Control>
          <QueryFilters.Control>
            <EnumDropdownMulti
              name="callVolumes"
              value={filtersState.callVolumes}
              enum={DispatchSummaryCallVolumeEnum}
              onChange={handleChange('callVolumes')}
              options={{ locale: { 'Select...': 'Call Volume' } }}
            />
          </QueryFilters.Control>
          <QueryFilters.Control>
            <Button className="border-white" name="SUBMIT" variant="success" onClick={handleSubmit} disabled={loading}>
              {loading && <i className="fa fa-spinner fa-pulse" />}
              {!loading && <span>Go</span>}
            </Button>
            <Button name="RESET" variant="outline-secondary-subtle" onClick={handleReset}>
              Reset
            </Button>
          </QueryFilters.Control>
          <QueryFilters.Control>
            <InputWithIcon
              icon={<i className="sv sv-magnifier fs-4" />}
              name="search"
              value={search || ''}
              onChange={handleChange('search')}
              placeholder="Search"
            />
          </QueryFilters.Control>
        </div>
        <div className="d-flex justify-content-end gap-2">
          <QueryFilters.Control>
            <FormButton
              maxWidth={1560}
              icon={<i className="sv sv-plus-square {font-size:1.5rem;}" />}
              name="CREATE_ACCIDENT"
              variant="outline-secondary-subtle"
              onClick={onCreate}
            >
              Create Dispatch Summary
            </FormButton>
          </QueryFilters.Control>
          <QueryFilters.Input>
            <PageNavigationDropdown />
          </QueryFilters.Input>
          <QueryFilters.Control>
            <FormButton
              icon={<i className="sv sv-trash2 {font-size:1.5rem;}" />}
              name="DELETE_LOST_AND_FOUND"
              variant="outline-secondary-subtle"
              onClick={onDelete}
              disabled={selected.size === 0}
            />
          </QueryFilters.Control>
          <QueryFilters.Control>
            <FormButton
              icon={<i className="sv sv-excel {font-size:1.5rem;}" />}
              name="EXPORT_AS_CSV"
              variant="outline-secondary-subtle"
              onClick={onExportToCSV}
              feedback="Export to CSV"
              disabled={summaries.size === 0}
            />
          </QueryFilters.Control>
          {/* <QueryFilters.Control>
            <FormButton
              variant="outline-secondary-subtle"
              onClick={onOpenSettings}
              icon={<i className="sv sv-cog {font-size:1.5rem;}" />}
              feedback="Accident History Page Settings & Preferences"
            />
          </QueryFilters.Control> */}
        </div>
      </QueryFilters>
      <SummaryHistoryFiltersInfo />
    </>
  );
};

export default SummaryHistoryFilters;
